"use client";

import { useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useEnhancedTimeline, type TimelineItem } from "@/lib/hooks/api/timeline/enhanced-timeline";
import { TimelineFiltersComponent } from "./TimelineFilters";
import { TimelineItemDetailModal } from "./TimelineItemDetailModal";
import { TimelineItemRow } from "./TimelineItemRow";
import { TimelineHeader } from "./TimelineHeader";
import { TimelineLoadingState, TimelineErrorState, TimelineEmptyState } from "./TimelineStates";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { groupTimelineByDay } from "../utils/timeline-grouping";
import { useTimelineState } from "./hooks/useTimelineState";
import { useTimelineNavigation } from "./hooks/useTimelineNavigation";
import { useUniversalAuth, useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { useContactByEmail } from "@/features/contacts/hooks";
import { toAuthenticatedUser } from "@/lib/utils/crm/user-adapters";

/** Minimal contact shape the timeline needs to resolve modal context. */
interface TimelineContact {
  id?: string | null;
}

interface EnhancedTimelineProps {
  email: string;
  client?: TimelineContact | null;
  className?: string;
  ticketId?: string;
  /** Contact ID for Log activity modal context (e.g. ticket's contact_id) */
  contactId?: string;
}

function getGmailThreadGroupKey(metadata: TimelineItem["metadata"]): string | null {
  if (!metadata || typeof metadata !== "object") return null;
  const raw = metadata as Record<string, unknown>;
  const id =
    typeof raw.threadId === "string" && raw.threadId.trim().length > 0
      ? raw.threadId.trim()
      : typeof raw.thread_id === "string" && raw.thread_id.trim().length > 0
        ? raw.thread_id.trim()
        : null;
  return id;
}

export const EnhancedTimeline = ({
  email,
  client,
  className = "",
  ticketId,
  contactId: propContactId,
}: EnhancedTimelineProps) => {
  // ✅ ALL HOOKS MUST BE CALLED FIRST, BEFORE ANY CONDITIONAL RETURNS
  const { appUser: rawAppUser } = useUniversalAuth();
  // Memoized: `toAuthenticatedUser` SPREADS into a new object, so calling it bare
  // here would hand every downstream dep array a fresh identity each render.
  // That is what silently killed the SimpleTable stable context slice.
  const appUser = useMemo(() => toAuthenticatedUser(rawAppUser), [rawAppUser]);
  const { openModal } = useGlobalModalStack();
  const emailValue = typeof email === "string" ? email.trim() : "";
  const hasEmail = emailValue.length > 0;
  const {
    data: contactFromEmail,
    error: contactError,
    isLoading: isContactLookupLoading,
  } = useContactByEmail(emailValue);
  const contact = useMemo(() => client || contactFromEmail, [client, contactFromEmail]);

  const {
    filters,
    sort,
    viewMode,
    selectedTimelineItem,
    isDetailModalOpen,
    limit,
    setFilters,
    setSort,
    setSelectedTimelineItem,
    setIsDetailModalOpen,
    handleToggleViewMode,
    handleClearFilters,
    handleLoadMore,
    hasFilters,
  } = useTimelineState();

  const {
    data: timeline,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useEnhancedTimeline(email, {
    filters,
    sort,
    limit,
    enabled:
      !!appUser &&
      (!!ticketId || hasEmail) &&
      (Boolean(ticketId) || (!isContactLookupLoading && Boolean(contact))),
    ticketId,
    contactId: contact?.id,
  });

  const { handleTimelineItemClick, handleModalClose, handleCopyItemLink } = useTimelineNavigation({
    timeline: timeline || [],
    onItemSelect: setSelectedTimelineItem,
    onModalOpen: setIsDetailModalOpen,
  });

  const { showingCount, hasMore } = useMemo(() => {
    const count = timeline?.length || 0;
    return { showingCount: count, hasMore: count >= limit };
  }, [timeline?.length, limit]);

  const threadEntriesByGmailThread = useMemo(() => {
    const timelineArray = timeline || [];
    const map = new Map<string, number>();
    for (const entry of timelineArray) {
      if (entry.type !== "communication") continue;
      const key = getGmailThreadGroupKey(entry.metadata);
      if (!key) continue;
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [timeline]);

  const dayGroups = useMemo(() => groupTimelineByDay(timeline || []), [timeline]);

  const threadPageCountFor = (timelineItem: TimelineItem): number | undefined => {
    if (timelineItem.type !== "communication") return undefined;
    const k = getGmailThreadGroupKey(timelineItem.metadata);
    if (!k) return undefined;
    return threadEntriesByGmailThread.get(k);
  };

  const handleRefresh = useCallback(() => refetch(), [refetch]);
  const handleOpenModal = useCallback(() => {
    const effectiveContactId =
      propContactId ||
      (contact?.id && !String(contact.id).startsWith("ticket-") ? contact.id : undefined);
    openModal(MODAL_IDS.messages.logActivity, {
      contactId: effectiveContactId,
      ticketId,
      userId: appUser?.id,
    });
  }, [openModal, contact?.id, propContactId, ticketId, appUser?.id]);

  // ✅ NOW CONDITIONAL RETURNS ARE SAFE - ALL HOOKS ALREADY CALLED
  if (!appUser) {
    return (
      <TimelineLoadingState
        className={className}
        isLoading
        message="Loading user context for timeline..."
      />
    );
  }

  if (!ticketId && contactError) {
    return (
      <TimelineErrorState
        className={className}
        isRefetching={isRefetching}
        onRefresh={handleRefresh}
        title="Failed to resolve contact"
        description={contactError.message || "Please refresh and try again."}
      />
    );
  }

  if (!ticketId && isContactLookupLoading) {
    return (
      <TimelineLoadingState
        className={className}
        isLoading={isContactLookupLoading}
        message="Looking up contact for this employee email..."
      />
    );
  }

  if (!contact && !ticketId) {
    return (
      <TimelineEmptyState
        className={className}
        hasFilters={false}
        onClearFilters={handleClearFilters}
        title="No CRM contact found for this employee email"
        description="Create or link a CRM contact to see timeline activity here."
      />
    );
  }

  if (error) {
    return (
      <TimelineErrorState
        className={className}
        isRefetching={isRefetching}
        onRefresh={handleRefresh}
      />
    );
  }

  if (!timeline || timeline.length === 0) {
    return (
      <TimelineEmptyState
        className={className}
        hasFilters={hasFilters}
        onClearFilters={handleClearFilters}
      />
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <TimelineHeader
        showingCount={showingCount}
        viewMode={viewMode}
        isRefetching={isRefetching}
        onToggleViewMode={handleToggleViewMode}
        onRefresh={handleRefresh}
        onOpenModal={handleOpenModal}
      />

      <div className="px-4">
        <TimelineFiltersComponent
          filters={filters}
          sort={sort}
          onFiltersChange={setFilters}
          onSortChange={setSort}
        />
      </div>

      <div className="px-1 sm:px-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          (() => {
            // Global row counter so the first EAGER_BODY_COUNT emails across all
            // day groups fetch their S3 body on mount; the rest lazy-load on scroll.
            let rowIndex = -1;
            const EAGER_BODY_COUNT = 3;
            return dayGroups.map((group) => (
              <div key={group.key} className="mb-2">
                <div className="sticky top-0 z-20 -mx-1 mb-1 bg-background/95 px-1 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur sm:-mx-4 sm:px-4">
                  {group.label}
                </div>
                {group.items.map((item, indexInGroup) => {
                  rowIndex += 1;
                  return (
                    <TimelineItemRow
                      key={`${item.type}-${item.id}`}
                      item={item}
                      compact={viewMode === "compact"}
                      threadEntriesOnPage={threadPageCountFor(item)}
                      eagerBody={rowIndex < EAGER_BODY_COUNT}
                      isLast={indexInGroup === group.items.length - 1}
                      onItemClick={handleTimelineItemClick}
                      onCopyLink={handleCopyItemLink}
                    />
                  );
                })}
              </div>
            ));
          })()
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center p-4">
          <Button onClick={handleLoadMore} variant="outline" size="sm">
            Load More Items
          </Button>
        </div>
      )}

      <TimelineItemDetailModal
        item={selectedTimelineItem}
        isOpen={isDetailModalOpen}
        onOpenChange={handleModalClose}
        onCopyLink={handleCopyItemLink}
      />
    </div>
  );
};
